// Deterministic models of sessions and cookies for Lesson 2.9 ("Remember Who Signed In").
// They simulate what the browser, the FastAPI server and its sessions table do with one
// session cookie; they do not set cookies, hash anything or send requests.

export const COOKIE = '__Host-fde_lab';
/** SESSION_SECONDS: 8 hours, the lesson's default session length. */
export const SESSION_SECONDS = 28800;
const SESSION_MINUTES = SESSION_SECONDS / 60;
/** The lab clock counts minutes from 00:00 on day 1 and starts at 09:00. */
export const START_MINUTES = 9 * 60;

export type LabUsername = 'maya' | 'ravi' | 'jo';
export interface LabAccount {
  id: number;
  name: string;
  username: LabUsername;
}
export const labAccounts: Record<LabUsername, LabAccount> = {
  maya: { id: 1, name: 'Maya', username: 'maya' },
  ravi: { id: 2, name: 'Ravi', username: 'ravi' },
  jo: { id: 3, name: 'Jo', username: 'jo' },
};
/** Users who log in somewhere in these graphics. */
export type SessionUser = 'maya' | 'ravi';

export interface ExampleToken {
  /** A secrets.token_urlsafe(32) value: 43 URL-safe characters. */
  token: string;
  /** hashlib.sha256(token.encode()).hexdigest(): the value the sessions table stores. */
  hash: string;
  /** First 12 hex characters of sha256(hash): what the server computes when a copied hash is sent as the cookie. */
  hashOfHash: string;
}
type TokenPool = readonly [ExampleToken, ExampleToken, ExampleToken];
// Fixed example values, generated once offline with Python 3: secrets.token_urlsafe(32) for
// each token, hashlib.sha256(token.encode()).hexdigest() for each hash, and
// hashlib.sha256(hash.encode()).hexdigest()[:12] for each hashOfHash. The model never hashes
// at runtime; session-models.test.ts recomputes every value with node:crypto.
const exampleTokens: Record<SessionUser, TokenPool> = {
  maya: [
    {
      token: 'Qm9eS90igxtj-zBHeYtCE76kJq9ODzuMUseap-wSGO4',
      hash: '005abdcb99f03228986385bd8998e8448c3be17f37ddef11876b484d47dc567f',
      hashOfHash: '07937a8f4068',
    },
    {
      token: '7YQSzi74twS1yzq1aniVlY6ZW09u0Iy6SINbeq3p32o',
      hash: '9f4453a673b146a05dabd3b7ade94e0ae812e56960ffc6b1473ce3effb5d8401',
      hashOfHash: 'f11dc9517bc1',
    },
    {
      token: 'lqk8rzmGErIqOLg-f6uNzN_JCqun3fi9A8BUkU6nE9M',
      hash: '7ea41e01cdc4a5732e6205f3b9b71092ea884435c96676a349c3065f3116eed7',
      hashOfHash: '501c26119f03',
    },
  ],
  ravi: [
    {
      token: 'lFKxttU8kRLlLUVXPEzNx0IO_jR_nT_KvzMdHvTt8Ys',
      hash: '3fab88120a05b90e921dd8549225ef28b9e9c052c316445ee589230a78d742f0',
      hashOfHash: '0a92d8307ef4',
    },
    {
      token: 'jgkEAU0SPiysn7ue1xmMX_rjyPX2dqwAUM27Q3o_XKE',
      hash: '9b9991d454f8781c27dd812188d08bb60b931c491bdb3f5202503c54037d1037',
      hashOfHash: '29130d2fdc16',
    },
    {
      token: 'KZHbesOEdCFDRz8Iae_2RpCaVmdMVISQYon-PJEGbYM',
      hash: '1d10ccdeebf1f4df0d8b58adfd2a8ebe8c2ae08c6bd0e44b2102f5a7469ac772',
      hashOfHash: '5b2e7af3e00d',
    },
  ],
};
export const allExampleTokens = (): ExampleToken[] => [
  ...exampleTokens.maya,
  ...exampleTokens.ravi,
];
/**
 * The token issued at a user's nth login (0-based). The first token is never reissued, so
 * Maya's first cookie stays recognisable; later logins alternate between the other two.
 * A user holds at most one row at a time in these graphics, so no two rows share a token.
 */
export function issuedToken(user: SessionUser, login: number): ExampleToken {
  const pool = exampleTokens[user];
  const index = login <= 0 ? 0 : 1 + ((login - 1) % (pool.length - 1));
  return pool[index] ?? pool[0];
}

export const shortToken = (token: string) => `${token.slice(0, 8)}…`;
export const shortHash = (hash: string) => `${hash.slice(0, 12)}…`;
export function formatClock(minutes: number) {
  const day = Math.floor(minutes / 1440) + 1;
  const within = minutes % 1440;
  const hours = String(Math.floor(within / 60)).padStart(2, '0');
  const mins = String(within % 60).padStart(2, '0');
  return `Day ${day} ${hours}:${mins}`;
}
export const setCookieHeader = (token: string) =>
  `set-cookie: ${COOKIE}=${shortToken(token)}; HttpOnly; Max-Age=${SESSION_SECONDS}; Path=/; SameSite=lax; Secure`;
export const CLEAR_COOKIE_HEADER = `set-cookie: ${COOKIE}=signed-out; HttpOnly; Max-Age=0; Path=/; SameSite=lax; Secure`;
const cookieLine = (value: string) => `cookie: ${COOKIE}=${value}`;
export const LOG_IN_FIRST = '{"detail":"Log in first."}';
const accountJson = (user: SessionUser) => {
  const account = labAccounts[user];
  return JSON.stringify({ id: account.id, name: account.name, username: account.username });
};
const TASKS_JSON =
  '[{"id":1,"title":"Prepare the demo","completed":false},{"id":4,"title":"Read Maya\'s notes","completed":true},…]';
const OK = 'HTTP/1.1 200 OK';
const UNAUTHORIZED = 'HTTP/1.1 401 Unauthorized';
const JSON_TYPE = 'content-type: application/json';
const loginRequest = (user: SessionUser, password: string, cookie: string | null) => [
  'POST /auth/login HTTP/1.1',
  JSON_TYPE,
  ...(cookie ? [cookieLine(shortToken(cookie))] : []),
  '',
  JSON.stringify({ username: user, password }),
];
const PASSWORDS: Record<SessionUser, string> = { maya: 'blue-kettle-42', ravi: 'summer2026' };

// --- session-flow: the second request proves who sent it with the cookie ----------------

export interface Exchange {
  /** The control that caused this exchange. */
  title: string;
  request: string[];
  response: string[];
  status: 200 | 401;
}
export interface FlowState {
  keepCookie: boolean;
  /** The token this browser stores, or null. */
  jar: string | null;
  logins: number;
  /** Tokens the server holds a live session for. */
  sessions: string[];
  exchanges: Exchange[];
  message: string;
}
export const initialFlow = (): FlowState => ({
  keepCookie: true,
  jar: null,
  logins: 0,
  sessions: [],
  exchanges: [],
  message: 'Nothing has been sent yet. Log in as Maya, then load /tasks.',
});
export type FlowAction = 'log-in' | 'load' | 'load-elsewhere' | 'toggle-keep' | 'reset';
const tasksExchange = (title: string, cookie: string | null, sessions: string[]): Exchange => {
  const known = cookie !== null && sessions.includes(cookie);
  return {
    title,
    request: ['GET /tasks HTTP/1.1', ...(cookie ? [cookieLine(shortToken(cookie))] : [])],
    response: known ? [OK, JSON_TYPE, '', TASKS_JSON] : [UNAUTHORIZED, JSON_TYPE, '', LOG_IN_FIRST],
    status: known ? 200 : 401,
  };
};
export function flowTransition(state: FlowState, action: FlowAction): FlowState {
  const record = (exchange: Exchange) => [...state.exchanges, exchange].slice(-6);
  switch (action) {
    case 'reset':
      return initialFlow();
    case 'toggle-keep': {
      if (state.keepCookie)
        return {
          ...state,
          keepCookie: false,
          jar: null,
          message: state.jar
            ? `The browser now refuses cookies and dropped ${COOKIE}. Load /tasks to see what the server receives.`
            : 'The browser now refuses cookies. Log in and watch what happens to set-cookie.',
        };
      return {
        ...state,
        keepCookie: true,
        message: 'The browser keeps cookies again, but it holds none. Log in to receive one.',
      };
    }
    case 'log-in': {
      const token = issuedToken('maya', state.logins).token;
      const exchange: Exchange = {
        title: 'Log in as Maya',
        request: loginRequest('maya', PASSWORDS.maya, state.jar),
        response: [OK, JSON_TYPE, setCookieHeader(token), '', accountJson('maya')],
        status: 200,
      };
      return {
        ...state,
        jar: state.keepCookie ? token : null,
        logins: state.logins + 1,
        sessions: [...state.sessions, token],
        exchanges: record(exchange),
        message: state.keepCookie
          ? 'The password matched. The server stored a session row for the token’s hash and sent the token itself in set-cookie. This browser keeps it for 8 hours (Max-Age=28800).'
          : 'The server created a session and sent set-cookie, but this browser did not keep the cookie. Its next request will carry nothing from this login.',
      };
    }
    case 'load': {
      const exchange = tasksExchange('Load /tasks', state.jar, state.sessions);
      return {
        ...state,
        exchanges: record(exchange),
        message:
          exchange.status === 200
            ? 'The browser added the cookie header by itself. The server hashed the token, found Maya’s session row and answered with the tasks.'
            : state.logins === 0
              ? 'This browser has no cookie yet, so the request carries nothing that names anyone. The server answers 401.'
              : 'The browser did not keep the cookie, so this request looks exactly like one from a stranger. The server answers 401.',
      };
    }
    case 'load-elsewhere':
      return {
        ...state,
        exchanges: record(tasksExchange('Load /tasks from another browser', null, state.sessions)),
        message:
          'The other browser never received set-cookie. Its request has no cookie header, so the server cannot connect it to Maya and answers 401.',
      };
  }
}

// --- cookie-attributes: what each attribute blocks --------------------------------------

export type CookieAttribute = 'httpOnly' | 'sameSite' | 'secure' | 'path';
export type CookieAttributes = Record<CookieAttribute, boolean>;
export const cookieAttributeLabels: Record<CookieAttribute, string> = {
  httpOnly: 'HttpOnly',
  sameSite: 'SameSite=Lax',
  secure: 'Secure',
  path: 'Path=/',
};
export const allAttributes = (): CookieAttributes => ({
  httpOnly: true,
  sameSite: true,
  secure: true,
  path: true,
});
export type ScenarioOutcome = 'Token exposed' | 'Cookie sent' | 'Blocked';
export interface ScenarioResult {
  scenario: string;
  attribute: CookieAttribute;
  outcome: ScenarioOutcome;
  detail: string;
  /** Whether this outcome is the one the application wants. */
  wanted: boolean;
}
export const HOST_PREFIX_RULE =
  'The __Host- prefix makes the browser refuse this cookie unless Secure and Path=/ are set.';
export const HOST_PREFIX_REFUSED = 'With the __Host- prefix, the browser would refuse this cookie.';
export const HOST_PREFIX_ACCEPTED =
  'Secure and Path=/ are set, so the browser accepts the __Host- name.';
export const hostPrefixRefused = (attributes: CookieAttributes) =>
  !attributes.secure || !attributes.path;
export function attributeHeader(attributes: CookieAttributes) {
  const parts = [
    `${COOKIE}=${shortToken(issuedToken('maya', 0).token)}`,
    ...(attributes.httpOnly ? ['HttpOnly'] : []),
    `Max-Age=${SESSION_SECONDS}`,
    attributes.path ? 'Path=/' : 'Path=/auth',
    ...(attributes.sameSite ? ['SameSite=lax'] : []),
    ...(attributes.secure ? ['Secure'] : []),
  ];
  return `set-cookie: ${parts.join('; ')}`;
}
type Effect = [ScenarioOutcome, string];
const scenario = (
  text: string,
  attribute: CookieAttribute,
  attributes: CookieAttributes,
  whenSet: Effect,
  whenMissing: Effect,
): ScenarioResult => {
  const set = attributes[attribute];
  const [outcome, detail] = set ? whenSet : whenMissing;
  // Every attribute protects the application, so its outcome is the wanted one.
  return { scenario: text, attribute, outcome, detail, wanted: set };
};
/** Each scenario depends on one attribute, so each row shows that attribute on its own. */
export function cookieScenarios(attributes: CookieAttributes): ScenarioResult[] {
  const token = shortToken(issuedToken('maya', 0).token);
  return [
    scenario(
      'A script injected into the page reads document.cookie',
      'httpOnly',
      attributes,
      ['Blocked', 'HttpOnly keeps the cookie out of document.cookie, so the script sees nothing.'],
      [
        'Token exposed',
        `document.cookie returns ${COOKIE}=${token}. The script can send Maya’s token to any server.`,
      ],
    ),
    scenario(
      'Another site submits a form that posts to /projects',
      'sameSite',
      attributes,
      [
        'Blocked',
        'SameSite=Lax keeps the cookie off a POST that starts on another site. The request arrives without it and gets 401.',
      ],
      [
        'Cookie sent',
        'The browser attaches Maya’s cookie to the other site’s post, so it reaches the server as Maya’s request.',
      ],
    ),
    scenario(
      'The page is opened over plain HTTP',
      'secure',
      attributes,
      [
        'Blocked',
        'Secure sends the cookie only over HTTPS, so the plain HTTP request carries nothing.',
      ],
      [
        'Token exposed',
        'The token travels unencrypted. Anyone who can watch the network, such as on shared Wi-Fi, can read it.',
      ],
    ),
    scenario(
      'The page loads /tasks',
      'path',
      attributes,
      [
        'Cookie sent',
        'Path=/ covers every path, so GET /tasks carries the cookie and the server finds Maya.',
      ],
      [
        'Blocked',
        'With Path=/auth the browser sends the cookie only to /auth paths. GET /tasks arrives without it and gets 401.',
      ],
    ),
  ];
}
export function cookieSummary(attributes: CookieAttributes) {
  const off = (Object.keys(cookieAttributeLabels) as CookieAttribute[]).filter(
    (key) => !attributes[key],
  );
  if (!off.length)
    return 'All four attributes are set. The script, the other site and plain HTTP get nothing, and /tasks still receives the cookie.';
  const effects: Record<CookieAttribute, string> = {
    httpOnly: 'HttpOnly is off: an injected script can read the token.',
    sameSite: 'SameSite=Lax is off: another site’s post carries Maya’s cookie.',
    secure: 'Secure is off: the token can travel unencrypted.',
    path: 'Path=/ is off: /tasks no longer receives the cookie, so the page gets 401.',
  };
  return off.map((key) => effects[key]).join(' ');
}

// --- session-store: what happens to a session row ---------------------------------------

export interface SessionRow {
  user: SessionUser;
  token: ExampleToken;
  expiresAt: number;
}
export interface StoreExchange {
  title: string;
  request: string[];
  server: string[];
  response: string[];
  status: 200 | 204 | 401;
}
export interface StoreState {
  now: number;
  rows: SessionRow[];
  logins: Record<SessionUser, number>;
  /** The token from Maya's first login: the cookie a copied or stale browser still holds. */
  oldCookie: ExampleToken | null;
  /** How the old cookie's row left the table, once it has. */
  oldCookieEnd: 'logged out' | 'removed after expiry' | null;
  last: StoreExchange | null;
  message: string;
}
export type StoreAction =
  | 'maya-log-in'
  | 'ravi-log-in'
  | 'maya-log-out'
  | 'advance'
  | 'old-cookie'
  | 'copied-hash'
  | 'reset';
export const initialStore = (): StoreState => ({
  now: START_MINUTES,
  rows: [],
  logins: { maya: 0, ravi: 0 },
  oldCookie: null,
  oldCookieEnd: null,
  last: null,
  message: 'The sessions table is empty and the clock reads Day 1 09:00. Log someone in.',
});
export const isLive = (row: SessionRow, now: number) => row.expiresAt > now;
const liveRow = (state: StoreState, user: SessionUser) =>
  state.rows.find((row) => row.user === user && isLive(row, state.now));
export function storeAvailable(state: StoreState): Record<Exclude<StoreAction, 'reset'>, boolean> {
  return {
    'maya-log-in': !liveRow(state, 'maya'),
    'ravi-log-in': !liveRow(state, 'ravi'),
    'maya-log-out': Boolean(liveRow(state, 'maya')),
    advance: true,
    'old-cookie': state.oldCookie !== null,
    'copied-hash': state.rows.length > 0,
  };
}
const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;
const rowLine = (row: SessionRow) =>
  `row: token_hash ${shortHash(row.token.hash)}, user_id ${labAccounts[row.user].id}, expires_at ${formatClock(row.expiresAt)}`;
function storeLogIn(state: StoreState, user: SessionUser): StoreState {
  const name = labAccounts[user].name;
  if (liveRow(state, user))
    return {
      ...state,
      message: `${name} already has a live session. Log out or advance the clock first.`,
    };
  const expired = state.rows.filter((row) => !isLive(row, state.now));
  const token = issuedToken(user, state.logins[user]);
  const row: SessionRow = { user, token, expiresAt: state.now + SESSION_MINUTES };
  const removedOld =
    state.oldCookie !== null && expired.some((item) => item.token === state.oldCookie);
  const oldCookie = state.oldCookie ?? (user === 'maya' ? token : null);
  return {
    ...state,
    rows: [...state.rows.filter((item) => isLive(item, state.now)), row],
    logins: { ...state.logins, [user]: state.logins[user] + 1 },
    oldCookie,
    oldCookieEnd: removedOld ? 'removed after expiry' : state.oldCookieEnd,
    last: {
      title: `${name} logs in`,
      request: loginRequest(user, PASSWORDS[user], null),
      server: [
        `DELETE FROM sessions WHERE expires_at <= now()  → ${plural(expired.length, 'row')} removed`,
        `INSERT INTO sessions: token_hash ${shortHash(token.hash)}, user_id ${labAccounts[user].id}, expires_at ${formatClock(row.expiresAt)}`,
      ],
      response: [OK, setCookieHeader(token.token), '', accountJson(user)],
      status: 200,
    },
    message: `${name} logged in. The table stores the hash of the new token with an expiry 8 hours from now (${formatClock(row.expiresAt)}); only ${name}’s browser holds the token itself.${
      expired.length ? ` Logging in first removed ${plural(expired.length, 'expired row')}.` : ''
    }`,
  };
}
export function storeTransition(state: StoreState, action: StoreAction): StoreState {
  switch (action) {
    case 'reset':
      return initialStore();
    case 'maya-log-in':
      return storeLogIn(state, 'maya');
    case 'ravi-log-in':
      return storeLogIn(state, 'ravi');
    case 'maya-log-out': {
      const row = liveRow(state, 'maya');
      if (!row) return { ...state, message: 'Maya has no live session to end.' };
      return {
        ...state,
        rows: state.rows.filter((item) => item !== row),
        oldCookieEnd: row.token === state.oldCookie ? 'logged out' : state.oldCookieEnd,
        last: {
          title: 'Maya logs out',
          request: ['POST /auth/logout HTTP/1.1', cookieLine(shortToken(row.token.token))],
          server: [
            `DELETE FROM sessions WHERE token_hash = ${shortHash(row.token.hash)}  → 1 row removed`,
          ],
          response: ['HTTP/1.1 204 No Content', CLEAR_COOKIE_HEADER],
          status: 204,
        },
        message:
          'Maya logged out. Her session row is gone, and the response told her browser to drop the cookie (Max-Age=0). A copy of her old token now names nobody.',
      };
    }
    case 'advance': {
      const now = state.now + SESSION_MINUTES;
      const expiring = state.rows.filter((row) => isLive(row, state.now) && !isLive(row, now));
      return {
        ...state,
        now,
        message: expiring.length
          ? `The clock moved to ${formatClock(now)}. ${plural(expiring.length, 'row')} reached expires_at and no longer count, because the lookup requires expires_at > now(). The rows stay in the table until the next login removes them.`
          : `The clock moved to ${formatClock(now)}. No live session row was affected.`,
      };
    }
    case 'old-cookie': {
      const old = state.oldCookie;
      if (!old)
        return { ...state, message: 'Maya has not logged in yet, so no cookie of hers exists.' };
      const row = state.rows.find((item) => item.token === old);
      const live = row !== undefined && isLive(row, state.now);
      const server = [`sha256(cookie) = ${shortHash(old.hash)}`];
      if (!row) server.push('no row has this token_hash');
      else
        server.push(
          rowLine(row),
          live
            ? `${formatClock(row.expiresAt)} > now ${formatClock(state.now)}: the session is valid`
            : `${formatClock(row.expiresAt)} <= now ${formatClock(state.now)}: the session has expired`,
        );
      return {
        ...state,
        last: {
          title: "Send Maya's old cookie",
          request: ['GET /tasks HTTP/1.1', cookieLine(shortToken(old.token))],
          server,
          response: live ? [OK, '', TASKS_JSON] : [UNAUTHORIZED, '', LOG_IN_FIRST],
          status: live ? 200 : 401,
        },
        message: live
          ? 'The cookie still matches a live row, so the server answers as Maya. Whoever holds this token is Maya until the row is deleted or expires.'
          : row
            ? 'The row is still in the table, but its expiry has passed. The lookup requires expires_at > now(), so the old cookie names nobody: 401.'
            : state.oldCookieEnd === 'logged out'
              ? 'Maya logged out, so no row has this hash any more. The old cookie names nobody: 401.'
              : 'The row expired and a later login removed it, so no row has this hash any more. The old cookie names nobody: 401.',
      };
    }
    case 'copied-hash': {
      const row = state.rows.find((item) => item.user === 'maya') ?? state.rows[0];
      if (!row)
        return { ...state, message: 'The sessions table is empty, so there is no hash to copy.' };
      const name = labAccounts[row.user].name;
      return {
        ...state,
        last: {
          title: 'Use a copied token hash as a cookie',
          request: ['GET /tasks HTTP/1.1', cookieLine(shortHash(row.token.hash))],
          server: [`sha256(cookie) = ${row.token.hashOfHash}…`, 'no row has this token_hash'],
          response: [UNAUTHORIZED, '', LOG_IN_FIRST],
          status: 401,
        },
        message: `The value was copied from ${name}’s row. The server hashes whatever arrives in the cookie, and the hash of a hash (${row.token.hashOfHash}…) matches no row: 401. A copied sessions table gives nobody a login.`,
      };
    }
  }
}

// --- auth-client-states: which screen the page shows -------------------------------------

export type ClientSession =
  | { kind: 'checking' }
  | { kind: 'signed-out'; notice: string }
  | { kind: 'signed-in'; account: LabAccount };
export const SESSION_ENDED = 'Your session ended. Log in again.';
export const WRONG_LOGIN = 'Wrong username or password.';
export type ScreenName = 'Checking your session…' | 'Log in form' | 'Signed in as Maya';
export interface ClientState {
  session: ClientSession;
  listener: boolean;
  /** The login form's fields and its status line; meaningful only while signed out. */
  form: { username: string; password: string; message: string };
  /** Load failures shown on the signed-in page. */
  errors: string[];
  trace: string[];
  message: string;
}
export type ClientEvent =
  'open-valid' | 'open-none' | 'log-in-right' | 'log-in-wrong' | 'load-401' | 'log-out';
export type ClientAction = ClientEvent | 'toggle-listener' | 'reset';
export const screenName = (session: ClientSession): ScreenName =>
  session.kind === 'checking'
    ? 'Checking your session…'
    : session.kind === 'signed-out'
      ? 'Log in form'
      : 'Signed in as Maya';
export function describeSession(session: ClientSession) {
  if (session.kind === 'checking') return "{ kind: 'checking' }";
  if (session.kind === 'signed-out') return `{ kind: 'signed-out', notice: '${session.notice}' }`;
  return `{ kind: 'signed-in', account: { id: ${session.account.id}, name: '${session.account.name}' } }`;
}
const emptyForm = (message = '') => ({ username: '', password: '', message });
export const initialClient = (): ClientState => ({
  session: { kind: 'checking' },
  listener: true,
  form: emptyForm(),
  errors: [],
  trace: [`page loads → session = ${describeSession({ kind: 'checking' })} → GET /auth/me`],
  message:
    'The page has just loaded. It shows Checking your session… while GET /auth/me is on its way. Choose what the browser holds.',
});
/** The lines the simulated page shows for the current state. */
export function clientScreen(state: ClientState): string[] {
  if (state.session.kind === 'checking') return ['Checking your session…'];
  if (state.session.kind === 'signed-out')
    return [
      'Log in',
      ...(state.form.message ? [state.form.message] : []),
      `Username: ${state.form.username || '(empty)'}`,
      `Password: ${state.form.password ? '••••••••' : '(empty)'}`,
    ];
  const { name, username } = state.session.account;
  return [
    `Signed in as ${name} (${username}) · Log out`,
    '',
    ...(state.errors.length ? state.errors : ['Tasks page: the task list loaded.']),
  ];
}
export function clientEventAllowed(state: ClientState, event: ClientEvent) {
  switch (event) {
    case 'open-valid':
    case 'open-none':
      return state.session.kind === 'checking';
    case 'log-in-right':
    case 'log-in-wrong':
      return state.session.kind === 'signed-out';
    case 'load-401':
    case 'log-out':
      return state.session.kind === 'signed-in';
  }
}
const failedLoads = [
  { request: 'GET /tasks', error: 'Could not load tasks. Log in first.' },
  { request: 'GET /projects', error: 'Could not load projects. Log in first.' },
  { request: 'GET /projects/1/tasks', error: 'Could not load project tasks. Log in first.' },
];
export function clientTransition(state: ClientState, action: ClientAction): ClientState {
  if (action === 'reset') return initialClient();
  if (action === 'toggle-listener')
    return {
      ...state,
      listener: !state.listener,
      message: state.listener
        ? 'The 401 listener is off. A 401 now reaches only the component that made the request.'
        : 'The 401 listener is on. Any 401 while signed in switches the page to the login form.',
    };
  if (!clientEventAllowed(state, action))
    return {
      ...state,
      message: `That event cannot happen while the page shows ${screenName(state.session)}.`,
    };
  const trace = (...lines: string[]) => [...state.trace, ...lines].slice(-8);
  const maya = labAccounts.maya;
  switch (action) {
    case 'open-valid':
      return {
        ...state,
        session: { kind: 'signed-in', account: maya },
        errors: [],
        trace: trace(
          `GET /auth/me → 200 ${accountJson('maya')}`,
          `session = ${describeSession({ kind: 'signed-in', account: maya })}`,
        ),
        message:
          'The cookie named a live session, so GET /auth/me answered with Maya’s account. The page went from Checking your session… straight to her tasks.',
      };
    case 'open-none':
      return {
        ...state,
        session: { kind: 'signed-out', notice: '' },
        form: emptyForm(),
        trace: trace(
          `GET /auth/me → 401 ${LOG_IN_FIRST}`,
          `session = ${describeSession({ kind: 'signed-out', notice: '' })}`,
        ),
        message:
          'The browser had no cookie, so GET /auth/me answered 401. That is the normal answer for someone who has not logged in, so the form shows no notice.',
      };
    case 'log-in-right':
      return {
        ...state,
        session: { kind: 'signed-in', account: maya },
        form: emptyForm(),
        errors: [],
        trace: trace(
          'POST /auth/login {"username":"maya","password":"…"} → 200 + set-cookie',
          `session = ${describeSession({ kind: 'signed-in', account: maya })}`,
        ),
        message:
          'The server checked the password, set the cookie and returned Maya’s account. The page switched to signed in without reloading.',
      };
    case 'log-in-wrong':
      return {
        ...state,
        form: { username: 'maya', password: '', message: WRONG_LOGIN },
        trace: trace(
          `POST /auth/login {"username":"maya","password":"…"} → 401 ${JSON.stringify({ detail: WRONG_LOGIN })}`,
          ...(state.listener ? ['401 listener → updater sees signed-out → no change'] : []),
        ),
        message: `The form shows the server’s message, keeps the username and clears the password, so only the password has to be typed again.${
          state.listener
            ? ' The 401 also reached the listener, but its updater read the current state, saw the page was already signed out and left it unchanged.'
            : ''
        }`,
      };
    case 'load-401': {
      const load =
        failedLoads[Math.min(state.errors.length, failedLoads.length - 1)] ?? failedLoads[0];
      if (!load) return state;
      if (state.listener)
        return {
          ...state,
          session: { kind: 'signed-out', notice: SESSION_ENDED },
          form: emptyForm(SESSION_ENDED),
          errors: [],
          trace: trace(
            `${load.request} → 401 ${LOG_IN_FIRST}`,
            `401 listener → updater sees signed-in → session = ${describeSession({ kind: 'signed-out', notice: SESSION_ENDED })}`,
          ),
          message:
            'The session ended on the server. The request helper called the 401 listener, whose updater saw the page was signed in and switched it to the login form with a notice.',
        };
      return {
        ...state,
        errors: state.errors.includes(load.error) ? state.errors : [...state.errors, load.error],
        trace: trace(`${load.request} → 401 ${LOG_IN_FIRST}`, 'no listener → session unchanged'),
        message:
          'Without the listener nothing changes the session state. The page still says Signed in as Maya, and every load fails with Log in first.',
      };
    }
    case 'log-out':
      return {
        ...state,
        session: { kind: 'signed-out', notice: '' },
        form: emptyForm(),
        errors: [],
        trace: trace(
          'POST /auth/logout → 204 + set-cookie Max-Age=0',
          `session = ${describeSession({ kind: 'signed-out', notice: '' })}`,
        ),
        message:
          'The page told the server first. The server deleted the session row and cleared the cookie, then the page showed the login form with no notice, because Maya chose to leave.',
      };
  }
}
